import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Form from 'react-bootstrap/Form'
import { selectTranslations } from '../../../services/i18n/i18nSlice'
import { setLoading } from '../../../services/global/globalSlice'
import { fetchCreateTopic, fetchDeleteTopic, fetchEditTopic, fetchQuestionCountByTopicId } from '../QuestionBankApi'
import TopicModal from './TopicModal'
import ConfirmModal from '../../../components/_modals/ConfirmModal'
import trash_img from '../../../assets/images/trash.svg'

const EMPTY_FORM = { topic_name: '', subject_id: '', icon_url: null }

export default function TopicsTab(props) {
    const t = useSelector(selectTranslations)
    const dispatch = useDispatch()

    const topics = props.topics
    const subjects = props.subjects
    const [questionCounts, setQuestionCounts] = useState({})
    const [loadingCounts, setLoadingCounts] = useState({})
    const [search, setSearch] = useState('')
    const [internalSubjectFilter, setInternalSubjectFilter] = useState('')

    const subjectFilter = props.subjectFilter !== undefined ? props.subjectFilter : internalSubjectFilter
    const setSubjectFilter = props.onSubjectFilterChange || setInternalSubjectFilter

    const [showModal, setShowModal] = useState(false)
    const [editingId, setEditingId] = useState(null)
    const [formData, setFormData] = useState(EMPTY_FORM)

    const [confirmModal, setConfirmModal] = useState({
        show: false, title: '', text: '', onClose: null, onConfirm: null, confirmCta: '', closeCta: ''
    })

    const subjectsById = useMemo(() => {
        const map = {}
        subjects?.forEach(s => { map[s.subject_id] = s.subject_name })
        return map
    }, [subjects])

    const filteredTopics = useMemo(() => {
        let _topics = topics
        if (subjectFilter) _topics = _topics.filter(top => top.subject_id === subjectFilter)
        if (search) {
            const s = search.toLowerCase()
            _topics = _topics.filter(top => (top?.topic_name || '').toLowerCase().includes(s))
        }
        return _topics
    }, [search, subjectFilter, topics])

    const loadQuestionCount = async (topic_id) => {
        setLoadingCounts(prev => ({ ...prev, [topic_id]: true }))
        const r = await fetchQuestionCountByTopicId(topic_id)
        if (r.success) setQuestionCounts(prev => ({ ...prev, [topic_id]: r.data?.question_count || 0 }))
        setLoadingCounts(prev => ({ ...prev, [topic_id]: false }))
    }

    const openNewTopic = () => {
        setEditingId(null)
        setFormData({ ...EMPTY_FORM, subject_id: subjectFilter || '' })
        setShowModal(true)
    }

    const openEditTopic = (topic) => {
        setEditingId(topic?.topic_id)
        setFormData({
            topic_name: topic?.topic_name || '',
            subject_id: topic?.subject_id || '',
            icon_url: topic?.icon_url || null
        })
        setShowModal(true)
    }

    const closeModal = () => setShowModal(false)

    const handleFormChange = (field, value) => {
        setFormData({ ...formData, [field]: value })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        dispatch(setLoading(true))
        const response = editingId
            ? await fetchEditTopic(editingId, formData)
            : await fetchCreateTopic(formData)
        if (response.success) {
            await props.onDataChange?.()
            closeModal()
        } else {
            toast.error(t.error.error_loading)
        }
        dispatch(setLoading(false))
    }

    const closeConfirmModal = () => setConfirmModal({ ...confirmModal, show: false })

    const performDeleteTopic = async (topic_id, force_delete = false) => {
        dispatch(setLoading(true))
        const response = await fetchDeleteTopic(topic_id, force_delete)
        dispatch(setLoading(false))
        if (response.success) {
            toast.success(t.question_bank.deleted_topic)
            closeConfirmModal()
            await props.onDataChange?.()
            return
        }
        if (!force_delete && response.error?.includes('used in')) {
            setConfirmModal({
                show: true,
                title: t.question_bank.force_delete_title,
                text: t.question_bank.force_delete_text,
                onClose: closeConfirmModal,
                onConfirm: () => performDeleteTopic(topic_id, true),
                confirmCta: t.question_bank.force_delete_cta,
                closeCta: t.modals.no_exit
            })
            return
        }
        toast.error(t.error.error_loading)
    }

    const handleDeleteTopic = (topic_id) => {
        setConfirmModal({
            show: true,
            title: t.question_bank.delete_topic_title,
            text: t.question_bank.delete_topic_text,
            onClose: closeConfirmModal,
            onConfirm: () => performDeleteTopic(topic_id, false),
            confirmCta: t.actions.delete,
            closeCta: t.modals.no_exit
        })
    }

    return (
        <section>
            <div className="info_card mb-3">
                <h2>{t.question_bank.search_filters}</h2>
                <div className="d-flex align-items-end flex-wrap mt-2">
                    <Form.Group className="mb-2 me-2" controlId="topic_search">
                        <Form.Label>{t.question_bank.search}</Form.Label>
                        <Form.Control type="text" placeholder={t.question_bank.search_placeholder} value={search} onChange={(e) => setSearch(e.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-2 me-2" controlId="topic_subject_filter">
                        <Form.Label>{t.question_bank.subject}</Form.Label>
                        <Form.Select value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)}>
                            <option value="">{t.question_bank.all_subjects}</option>
                            {subjects?.map(s => (
                                <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>
            </div>

            <div className="d-flex justify-content-end mb-3">
                <button onClick={openNewTopic}>{t.question_bank.new_topic}</button>
            </div>

            <div className="table-responsive">
                <table className="table">
                    <thead>
                        <tr>
                            <th>{t.question_bank.id}</th>
                            <th>{t.question_bank.topic_name}</th>
                            <th>{t.question_bank.subject}</th>
                            <th>{t.question_bank.available_questions}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTopics?.length ? filteredTopics.map((topic) => (
                            <tr key={topic.topic_id}>
                                <td>{topic.topic_id}</td>
                                <td>{topic.topic_name}</td>
                                <td>{subjectsById[topic.subject_id] || ''}</td>
                                <td>
                                    {questionCounts[topic.topic_id] !== undefined ? questionCounts[topic.topic_id] : (
                                        <button type="button" className="secondary" disabled={!!loadingCounts[topic.topic_id]} onClick={() => loadQuestionCount(topic.topic_id)}>
                                            {loadingCounts[topic.topic_id] ? t.question_bank.calculating : t.question_bank.calculate}
                                        </button>
                                    )}
                                </td>
                                <td>
                                    <div className="d-flex justify-content-end">
                                        <button className="secondary me-2" onClick={() => openEditTopic(topic)}>{t.actions.open}</button>
                                        <button className="secondary" onClick={() => handleDeleteTopic(topic.topic_id)}>
                                            <img src={trash_img} alt="" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={5} className="text-center">{t.question_bank.no_topics}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <TopicModal
                show={showModal}
                isEditing={!!editingId}
                formData={formData}
                subjects={subjects}
                onChange={handleFormChange}
                onSubmit={handleSubmit}
                onClose={closeModal}
            />

            <ConfirmModal props={confirmModal} />
        </section>
    )
}
